<template>
  <div class="product-group-dialog-content">
    <v-card
      class="d-flex flex-column"
      tile
      :loading="isLoading ? 'secondary' : false"
      :max-height="!isMobile ? '612px' : '100vh'"
      :min-height="!isMobile ? '612px' : ''"
    >
      <v-toolbar
        style="height: auto;"
        elevation="0"
        class="py-3 pr-4 toolbar-with-subheader flex-grow-0 flex-shrink-0"
        color="white"
      >
        <div class="toolbar-titles-container overflow-hidden">
          <template v-if="isLoading">
            <v-toolbar-title class="pl-5 mt-1">
              <v-skeleton-loader
                type="heading"
                style="width: 200px; max-width: 200px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
            <v-toolbar-title class="toolbar-subheader pl-5 mt-2">
              <v-skeleton-loader
                type="text"
                style="width: 120px; max-width: 120px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
          </template>
          <template v-else>
            <v-toolbar-title class="pl-5">{{ productGroup?.name }}</v-toolbar-title>
            <v-toolbar-title class="pl-5 toolbar-subheader">{{ subtitle }}</v-toolbar-title>
          </template>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon @click="startEdit" :disabled="isLoading || !productGroup">
          <v-icon>mdi-pen</v-icon>
        </v-btn>
      </v-toolbar>
      <v-tabs
        v-model="tabIndex"
        color="secondary"
        class="flex-grow-0 flex-shrink-0"
        grow
      >
        <v-tab href="#product-group-data">
          Information
        </v-tab>
        <v-tab href="#product-group-history">
          Historik
        </v-tab>
        <v-tab href="#product-group-sales-stats">
          Statistik
        </v-tab>
        <v-tab href="#product-group-products">
          <v-badge
            :value="productsCount > 0"
            color="secondary"
            :content="productsCount"
            offset-x="-2"
            offset-y="8"
          >
            <span>Produkter</span>
          </v-badge>
        </v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-tabs-items v-model="tabIndex" class="flex-grow-1 overflow-y-auto" style="min-height: 0;">
        <!-- Tab 1: Produktgruppsinformation -->
        <v-tab-item value="product-group-data">
          <v-sheet class="flex-grow-1 pa-4">
            <template v-if="isLoading">
              <v-card-text class="pa-6">
                <v-skeleton-loader type="list-item-three-line, list-item-three-line"></v-skeleton-loader>
              </v-card-text>
            </template>
            <template v-else-if="productGroup">
              <v-list subheader>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-bank</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ accountName }}</v-list-item-title>
                    <v-list-item-subtitle>Konto</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-percent</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ vatName }}</v-list-item-title>
                    <v-list-item-subtitle>Momssats</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-package-variant</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ productTypeName }}</v-list-item-title>
                    <v-list-item-subtitle>Typ</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="productGroup.description">
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-text</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ productGroup.description }}</v-list-item-title>
                    <v-list-item-subtitle>Beskrivning</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-information-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ productGroup.status_text }}</v-list-item-title>
                    <v-list-item-subtitle>Status</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </v-sheet>
        </v-tab-item>

        <!-- Tab 2: Historik -->
        <v-tab-item value="product-group-history">
          <v-sheet class="flex-grow-1 pa-4">
            <editing-record-timeline
              :editing-records="editingRecords"
              :loading="isLoading"
            ></editing-record-timeline>
          </v-sheet>
        </v-tab-item>

        <!-- Tab 3: Försäljningsstatistik -->
        <v-tab-item value="product-group-sales-stats">
          <v-sheet class="flex-grow-1 pa-4">
            <template v-if="isLoading">
              <v-skeleton-loader type="image"></v-skeleton-loader>
            </template>
            <template v-else-if="productGroup">
              <sales-history-chart
                :product-id="productGroup.id"
                :prefetched-data="salesHistory"
              ></sales-history-chart>
              <v-simple-table class="mt-6" v-if="salesHistory">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Månad</th>
                      <th class="text-right">Omsättning</th>
                      <th class="text-right">Sålda enheter</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in salesHistoryReversed" :key="i">
                      <td>{{ row.month.format('MMMM YYYY') }}</td>
                      <td class="text-right">{{ formatTurnover(row.total_sales) }}</td>
                      <td class="text-right">{{ Math.round(row.total_quantity / 1000) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
          </v-sheet>
        </v-tab-item>

        <!-- Tab 4: Produkter i gruppen -->
        <v-tab-item value="product-group-products">
          <v-sheet class="flex-grow-1">
            <template v-if="isLoadingProducts && groupProducts.length === 0">
              <v-card-text>
                <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
              </v-card-text>
            </template>
            <template v-else-if="groupProducts.length > 0">
              <v-data-table
                class="product-group-products-table"
                :headers="productHeaders"
                :items="groupProducts"
                fixed-header
                hide-default-footer
                disable-pagination
                :items-per-page="-1"
                virtual-rows
              >
                <template v-slot:item="{ item }">
                  <tr class="cursor-pointer" @click="openProduct(item)">
                    <td>{{ item.name }}</td>
                    <td>{{ formatPrice(item.price) }}</td>
                    <td class="text-right">{{ formatTurnover(item.turnover_12_months) }}</td>
                  </tr>
                </template>
              </v-data-table>
              <div class="d-flex justify-center pa-4" v-if="groupProducts.length < groupProductsTotalCount">
                <v-btn
                  text
                  color="secondary"
                  :loading="isLoadingProducts"
                  @click="loadMoreProducts"
                >
                  Ladda fler ({{ groupProducts.length }} av {{ groupProductsTotalCount }})
                </v-btn>
              </div>
            </template>
            <template v-else>
              <v-alert
                dense
                text
                color="info"
                class="ma-4 text-center"
              >
                Produktgruppen har inga aktiva produkter
              </v-alert>
            </template>
          </v-sheet>
        </v-tab-item>
      </v-tabs-items>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          text
          @click="newProduct"
        >
          Lägg till ny produkt
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import SalesHistoryChart from '@/components/product/SalesHistoryChart.vue'
import EditingRecordTimeline from '@/components/common/EditingRecordTimeline.vue'
import ProductService from '@/services/ProductService.js'

export default {
  name: 'ProductGroupDialogContent',
  components: {
    SalesHistoryChart,
    EditingRecordTimeline
  },
  props: {
    productGroup: Object,
    salesHistory: Array,
    editingRecords: {
      type: Array,
      default: () => []
    },
    isLoading: Boolean,
    isMobile: Boolean,
    show: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    tabIndex: 'product-group-data',
    groupProducts: [],
    groupProductsTotalCount: 0,
    isLoadingProducts: false
  }),
  computed: {
    subtitle: function () {
      if (!this.productGroup) {
        return ''
      }
      return `Produktgrupp · ${this.productsCount} produkter`
    },
    accountName: function () {
      if (!this.productGroup || !this.productGroup.account) {
        return ''
      }
      return `${this.productGroup.account.account_number} ${this.productGroup.account.name}`
    },
    vatName: function () {
      if (!this.productGroup || !this.productGroup.tax) {
        return ''
      }
      return this.productGroup.tax.name
    },
    productTypeName: function () {
      if (!this.productGroup) {
        return ''
      }
      return this.productGroup.product_type === 'goods' ? 'Varor' : 'Tjänster'
    },
    productsCount: function () {
      if (!this.productGroup) {
        return 0
      }
      return this.productGroup.products_count || 0
    },
    salesHistoryReversed: function () {
      if (!this.salesHistory) {
        return []
      }
      return [...this.salesHistory].reverse()
    },
    productHeaders: function () {
      return [
        { text: 'Namn', align: 'start', sortable: false, value: 'name' },
        { text: 'Pris inkl. moms', sortable: false, value: 'price' },
        { text: 'Omsättning 12m', align: 'end', sortable: false, value: 'turnover_12_months' }
      ]
    }
  },
  methods: {
    close: function () {
      this.$emit('close')
    },
    newProduct: function () {
      if (!this.productGroup) {
        return
      }
      this.$store.dispatch('product/openNewProductFormDialog', { productGroupId: this.productGroup.id })
    },
    formatPrice: function (priceInOre) {
      if (priceInOre === null) {
        return 'Saknar bestämt pris'
      }
      return window.conversion.toHumanReadableAmount(priceInOre / 100)
    },
    formatTurnover: function (turnoverInOre) {
      return window.conversion.toHumanReadableAmount((turnoverInOre || 0) / 100, { roundToInteger: true })
    },
    openProduct: function (product) {
      this.$store.dispatch('product/openDialogById', { productId: product.id, smaller: true })
    },
    fetchProducts: function () {
      if (!this.productGroup) {
        return
      }
      this.isLoadingProducts = true
      ProductService.getProductGroupProducts({
        productGroupId: this.productGroup.id,
        offset: this.groupProducts.length,
        limit: 50
      })
        .then(({ data }) => {
          if (data.status === 'success') {
            this.groupProducts = this.groupProducts.concat(data.data.products)
            this.groupProductsTotalCount = data.data.total_count
          }
        })
        .finally(() => {
          this.isLoadingProducts = false
        })
    },
    loadMoreProducts: function () {
      this.fetchProducts()
    },
    startEdit: function () {
      if (!this.productGroup) {
        return
      }
      this.$store.dispatch('product/startEditProductGroup', { productGroup: this.productGroup })
    }
  },
  watch: {
    productGroup: function () {
      this.groupProducts = []
      this.groupProductsTotalCount = 0
    },
    tabIndex: function (newTab) {
      if (newTab === 'product-group-products' && this.groupProducts.length === 0 && this.productGroup) {
        this.fetchProducts()
      }
    },
    show: function () {
      if (!this.show) {
        this.tabIndex = 'product-group-data'
      }
    }
  }
}
</script>
